import React, { useRef, useState } from "react";
import { Pagination, Paper, Stack, useTheme } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ResourceChangeLogRow from "./ChangeLogRow";

dayjs.extend(utc);

interface ResourceChangeLogProps {
  history: HistoryResponse;
}

export type HistoryResponse = Array<ResourceChange>;
export type ResourceAction = "create" | "duplicate" | "reassign" | "remove" | "restore" | "share" | "update";
export type ResourceType = "dashboard" | "dashboard_page" | "dashboard_widget";

export interface ResourceChange {
  action: ResourceAction;
  as_admin: boolean;
  resource_id: string;
  resource_type: ResourceType;
  timestamp: string;
  user_email: string;
}

type ChangeMap = Record<string, ResourceChange[]>;

export default function ResourceChangeLog(props: ResourceChangeLogProps): React.JSX.Element {
  const theme = useTheme();

  const [page, setPage] = useState<number>(1);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const changeMap = Object.groupBy(props.history, (change: ResourceChange) => {
    return change.timestamp.split("T")[0];
  }) as ChangeMap;

  const uniqueDates = Object.keys(changeMap).sort().reverse();

  const pageSize = 20;
  const pageCount = Math.ceil(uniqueDates.length / pageSize);

  const activeDates = uniqueDates.slice((page-1) * pageSize, page * pageSize);

  return (
    <Stack sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflow: "hidden"
    }}>
      <Stack 
        ref={scrollRef}
        sx={{
          flex: "1 1 auto",
          gap: 1,
          overflowY: "auto",
          p: 1
        }}
      >
        {activeDates.map((date) => {
          const changes = changeMap[date];
          return <ResourceChangeLogRow key={date} changes={changes} date={date} />;
        })}
      </Stack>
      {pageCount > 1 &&
        <Paper square sx={{p: 1}} variant="outlined">
          <Pagination
            count={pageCount}
            onChange={(event, value) => {
              setPage(value);
              if (scrollRef.current) {
                scrollRef.current.scrollTo({behavior: "smooth", top: 0})
              }
            }}
            page={page}
            sx={{justifySelf: "center"}}
          />
        </Paper>
      }
    </Stack>
  )
}